import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { useTheme } from "@/context/ThemeContext";
import { FavoritesProvider } from "@/context/FavoritesContext";
import api, { money } from "@/lib/api";
import { Amount } from "@/components/shared/Amount";
import { UserAvatar } from "@/components/shared/UserAvatar";
import { changeAvatar } from "@/lib/avatarUpload";
import { Logo } from "@/components/shared/Logo";
import { LanguageSwitcher } from "@/components/shared/LanguageSwitcher";
import { WelcomeLangBanner } from "@/components/shared/WelcomeLangBanner";
import { EmailVerifyBanner } from "@/components/shared/EmailVerifyBanner";
import {
  PlusCircle, ShoppingBag, ListOrdered, Wallet, Receipt, Code2, LifeBuoy,
  RefreshCw, HelpCircle, LogOut, Bell, Menu, X, Plus, User, Moon, Sun, ChevronRight, Camera, Heart, Gift, Network,
} from "lucide-react";

const GROUPS = [
  {
    title: "Menu principal", tkey: "client.nav.group.main", items: [
      { to: "/dashboard", label: "Nouvelle commande", tkey: "client.nav.new_order", icon: PlusCircle, end: true },
      { to: "/dashboard/refill", label: "Refill", tkey: "client.nav.refill", icon: RefreshCw },
      { to: "/dashboard/services", label: "Services", tkey: "client.nav.services", icon: ShoppingBag },
      { to: "/dashboard/favorites", label: "Mes favoris", tkey: "client.nav.favorites", icon: Heart },
      { to: "/dashboard/orders", label: "Historique", tkey: "client.nav.history", icon: ListOrdered },
    ],
  },
  {
    title: "Compte & facturation", tkey: "client.nav.group.billing", items: [
      { to: "/dashboard/add-funds", label: "Ajouter des fonds", tkey: "client.nav.add_funds", icon: Wallet },
      { to: "/dashboard/transactions", label: "Transactions", tkey: "client.nav.transactions", icon: Receipt },
      { to: "/dashboard/api", label: "API", tkey: "client.nav.api", icon: Code2 },
    ],
  },
  {
    title: "Partenaire", tkey: "client.nav.group.partner", items: [
      { to: "/dashboard/partner", label: "Parrainage & Bonus", tkey: "client.nav.partner", icon: Gift, end: true },
      { to: "/dashboard/partner/binary-tree", label: "Mon Arbre Binaire", tkey: "client.nav.binary_tree", icon: Network },
    ],
  },
  {
    title: "Support", tkey: "client.nav.group.support", items: [
      { to: "/faq", label: "FAQ", tkey: "client.nav.faq", icon: HelpCircle },
      { to: "/dashboard/support", label: "Tickets", tkey: "client.nav.tickets", icon: LifeBuoy },
    ],
  },
];

const initials = (name = "") => name.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "U";

export function DashboardLayout() {
  const { user, logout, setUser } = useAuth();
  const { t } = useContent();
  const { isDark, toggle } = useTheme();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [balance, setBalance] = useState(0);
  const [notifs, setNotifs] = useState([]);
  const [showNotif, setShowNotif] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const notifRef = useRef(null);
  const profRef = useRef(null);

  const loadTop = async () => {
    try {
      const [w, n] = await Promise.all([api.get("/wallet"), api.get("/notifications")]);
      setBalance(w.data.balance); setNotifs(n.data);
    } catch { /* ignore */ }
  };
  const loadNotifs = async () => {
    try { const n = await api.get("/notifications"); setNotifs(n.data); } catch { /* ignore */ }
  };
  useEffect(() => { loadTop(); }, [loc.pathname]);
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  // Near real-time notifications: poll every 10s, pause when tab hidden, refetch on regaining focus.
  useEffect(() => {
    let timer = null;
    const tick = () => { if (document.visibilityState === "visible") loadNotifs(); };
    const onVisible = () => { if (document.visibilityState === "visible") loadNotifs(); };
    timer = setInterval(tick, 10000);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", onVisible); window.removeEventListener("focus", onVisible); };
  }, []);

  useEffect(() => {
    const h = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotif(false);
      if (profRef.current && !profRef.current.contains(e.target)) setShowProfile(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const unread = notifs.filter((n) => !n.read).length;
  const openNotif = async () => { setShowNotif((v) => !v); if (unread) { await api.post("/notifications/read"); loadTop(); } };
  const isActive = (n) => (n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to));

  const Sidebar = (
    <aside className={`fixed inset-y-0 left-0 z-[80] flex w-72 flex-col border-r border-border bg-card transition-transform lg:static lg:z-0 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
      <div className="flex h-16 items-center justify-between gap-2 border-b border-border px-4">
        <Link to="/dashboard" className="flex items-center gap-2">
          <Logo alt="Logo" className="h-8 w-8 [filter:brightness(0)] dark:[filter:brightness(0)_invert(1)]" />
          <span className="font-display text-sm font-bold tracking-tight">The World Promotions</span>
        </Link>
        <button className="-mr-2 flex h-10 w-10 items-center justify-center rounded-lg hover:bg-secondary lg:hidden" onClick={() => setOpen(false)} data-testid="close-sidebar" aria-label="close menu"><X className="h-5 w-5" /></button>
      </div>

      {/* Balance card */}
      <div className="m-3 rounded-2xl border border-border bg-gradient-to-br from-accent/15 via-accent/5 to-transparent p-4">
        <div className="text-xs font-medium text-muted-foreground">{t("client.balance", "Solde disponible")}</div>
        <div className="mt-1.5 flex items-center justify-between">
          <span data-testid="sidebar-balance" className="font-display text-2xl font-bold tracking-tight"><Amount value={balance} /></span>
          <Link to="/dashboard/add-funds" data-testid="sidebar-add-funds" className="flex h-9 w-9 items-center justify-center rounded-full bg-accent text-white shadow-sm transition-transform hover:scale-105">
            <Plus className="h-5 w-5" />
          </Link>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 pb-4">
        {GROUPS.map((g) => (
          <div key={g.title} className="mb-4">
            <div className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">{t(g.tkey, g.title)}</div>
            <div className="space-y-1">
              {g.items.map((n) => (
                <Link key={n.to} to={n.to} data-testid={`sidebar-${n.to}`}
                  className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${isActive(n) ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
                  <n.icon className="h-[18px] w-[18px]" /> {t(n.tkey, n.label)}
                </Link>
              ))}
            </div>
          </div>
        ))}
      </nav>
    </aside>
  );

  return (
    <div className="flex min-h-screen bg-secondary/30">
      {Sidebar}
      {open && <div className="fixed inset-0 z-[75] bg-black/50 lg:hidden" onClick={() => setOpen(false)} />}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bg-background/80 px-4 backdrop-blur-xl sm:px-6">
          <div className="flex items-center gap-2">
            <button className="-ml-2 flex h-10 w-10 items-center justify-center rounded-lg hover:bg-secondary lg:hidden" onClick={() => setOpen(true)} data-testid="open-sidebar" aria-label="open menu"><Menu className="h-6 w-6" /></button>
            <Link to="/dashboard" className="flex items-center gap-2 lg:hidden">
              <Logo alt="Logo" className="h-7 w-7 [filter:brightness(0)] dark:[filter:brightness(0)_invert(1)]" />
              <span className="hidden font-display text-sm font-bold md:inline">TWP</span>
            </Link>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <button data-testid="header-theme-toggle" onClick={toggle} aria-label="theme"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card transition-colors hover:bg-secondary">
              {isDark ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
            </button>
            <LanguageSwitcher />
            {/* Notifications */}
            <div className="relative" ref={notifRef}>
              <button data-testid="notif-btn" onClick={openNotif} className="relative flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card transition-colors hover:bg-secondary">
                <Bell className="h-[18px] w-[18px]" />
                {unread > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[9px] font-bold text-white">{unread}</span>}
              </button>
              {showNotif && (
                <div className="fixed left-4 right-4 top-[4.5rem] w-auto max-w-sm rounded-2xl border border-border bg-popover p-2 shadow-xl sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96" data-testid="notif-panel">
                  <div className="px-2 py-1.5 font-display text-sm font-semibold">{t("client.notifs", "Notifications")}</div>
                  <div className="max-h-80 overflow-auto">
                    {notifs.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">{t("client.no_notifs", "Aucune notification")}</div>}
                    {notifs.slice(0, 12).map((n) => (
                      <div key={n.id} className="rounded-lg p-2 hover:bg-secondary">
                        <div className="text-sm font-medium">{n.title}</div>
                        <div className="text-xs text-muted-foreground">{n.message}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Profile */}
            <div className="relative" ref={profRef}>
              <button data-testid="profile-btn" onClick={() => setShowProfile((v) => !v)}
                className="transition-transform hover:scale-105">
                <UserAvatar user={user} testid="header-avatar" className="h-10 w-10 rounded-full bg-primary font-display text-xs font-bold text-primary-foreground" />
              </button>
              {showProfile && (
                <div className="absolute right-0 mt-2 w-56 rounded-2xl border border-border bg-popover p-1.5 shadow-xl" data-testid="profile-menu">
                  <div className="border-b border-border px-3 py-2">
                    <div className="truncate text-sm font-semibold">{user?.name}</div>
                    <div className="truncate text-xs text-muted-foreground">{user?.email}</div>
                  </div>
                  <Link to="/dashboard/settings" data-testid="menu-profile" onClick={() => setShowProfile(false)}
                    className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors hover:bg-secondary">
                    <User className="h-4 w-4" /> {t("client.menu.profile", "Voir le profil")}
                  </Link>
                  <button data-testid="menu-change-photo" onClick={async () => { setShowProfile(false); await changeAvatar(setUser, t); }}
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors hover:bg-secondary">
                    <Camera className="h-4 w-4" /> {t("client.menu.change_photo", "Changer la photo")}
                  </button>
                  <button data-testid="menu-theme-toggle" onClick={toggle}
                    className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors hover:bg-secondary">
                    <span className="flex items-center gap-3">{isDark ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />} {t("client.menu.dark", "Mode sombre")}</span>
                    <span className={`relative h-5 w-9 rounded-full transition-colors ${isDark ? "bg-accent" : "bg-muted-foreground/30"}`}>
                      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${isDark ? "translate-x-4" : "translate-x-0.5"}`} />
                    </span>
                  </button>
                  <button data-testid="menu-logout" onClick={() => { logout(); nav("/"); }}
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-destructive transition-colors hover:bg-destructive/10">
                    <LogOut className="h-4 w-4" /> {t("client.menu.logout", "Se déconnecter")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 pb-24 sm:p-6 lg:p-8"><FavoritesProvider><EmailVerifyBanner /><WelcomeLangBanner /><Outlet context={{ reloadTop: loadTop, balance }} /></FavoritesProvider></main>
      </div>
    </div>
  );
}
